import {Component, EventEmitter, inject, Input, OnDestroy, Output} from '@angular/core';
import {Currencies} from "../../interfaces/currencies";
import {OrdersForPay, OrdersForPayData} from "../../interfaces/orders-for-pay";
import {RequestData} from "../../interfaces/request-data";
import {finalize, takeUntil} from "rxjs/operators";
import {BaseService} from "../../services/base.service";
import {Subject} from "rxjs";
import {HttpClient} from "@angular/common/http";
import * as moment from "moment";

@Component({
  selector: 'app-modal-payment',
  templateUrl: './modal-payment.component.html',
  styleUrls: ['./modal-payment.component.scss']
})
export class ModalPaymentComponent extends BaseService implements OnDestroy {
  @Input() payment;

  @Input() cards;

  @Input() getCurrencies: Currencies[];

  @Input() ordersForPay: OrdersForPayData[];

  @Output() modalSendPaymentChange: EventEmitter<any> = new EventEmitter<any>();

  @Output() modalTogglePaymentForGoodsChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  private http: HttpClient = inject(HttpClient);

  public isDestroyed: Subject<boolean> = new Subject();
  public isModalPaymentShow: boolean = false;
  public isDarkBackground: boolean = false;
  public isModalCardsShow: boolean = false;
  public checkedOrder: boolean = true;
  public isValidationForm: boolean = false;
  // Поки запит price/pay_notify у роботі - «Відправити» заблокована: кнопка лишається
  // у фокусі, і Enter/пробіл під лоадером відправляли повідомлення про оплату повторно.
  public isSendingPayNotify: boolean = false;
  public selectedCardFop: any = null;

  setSelectedCardFopById(id: any) {
    this.selectedCardFop = this.cards?.find(c => String(c.id) === String(id)) ?? null;
  }

  modalPaymentShow() {
    this.payment.choused_key ??= '';
    this.payment.choused_key_error ??= false;
    // payment живе в шапці між відкриттями: без скидання вікно відкривалось
    // з червоними полями й «Виберіть замовлення» від минулої невдалої спроби
    this.payment.pay_card_error = false;
    this.payment.pay_summa_error = false;
    this.payment.pay_comment_error = false;
    this.checkedOrder = true;
    // «Так, це ще один платіж» стосується лише того вікна, де клієнт його підтвердив. Якщо повтор
    // обірвався (помилка, «Скасувати» в лоадері), прапорець лишався true і наступне повідомлення
    // в цій шапці йшло без перевірки бека на вже надіслану оплату - дубль повідомлення
    this.payment.send_pay_notify = false;

    this.http.get(this.apiRoot + 'header/get_order_info')
      .pipe(takeUntil(this.isDestroyed))
      .subscribe((data: OrdersForPay) => {
        this.ordersForPay = data.data;

        if (this.ordersForPay && this.ordersForPay.length) {
          for (let order of this.ordersForPay) {
            order.date = moment(order.date).format('DD.MM.YYYY');
            order.summa_rate = Math.ceil(order.summa * order.rate);
            order.isOrderChecked = this.ordersForPay.length === 1;
          }
        }

        if (this.payment?.pay_card) {
          this.setSelectedCardFopById(this.payment.pay_card);
        }

        this.isModalCardsShow = true;
        this.isModalPaymentShow = true;
        this.isDarkBackground = true;
      });
  }

  onPayCardChange() {
    this.setSelectedCardFopById(this.payment.pay_card);

    this.payCardValidation();

    if (!this.payment.pay_card || this.selectedCardFop?.req_type !== 'fop_req') {
      this.payment.choused_key = '';
      this.payment.choused_key_error = false;
    }

    this.commentValidation();
  }

  onFopRouteChange() {
    this.payment.choused_key_error = this.selectedCardFop?.req_type === 'fop_req' && !this.payment.choused_key;
  }

  payCardValidation() {
    if (this.payment.pay_card) {
      this.payment.pay_card_error = false;
    } else {
      this.payment.pay_card_error = true;
      this.isValidationForm = true;
    }

    if (this.selectedCardFop?.req_type === 'fop_req') {
      this.payment.choused_key_error = !this.payment.choused_key;
      if (this.payment.choused_key_error) {
        this.isValidationForm = true;
      }
    } else {
      this.payment.choused_key_error = false;
    }

    this.commentValidation();
  }

  paySummaValidation() {
    // бек відсікає лише (int) == 0, тож «-500» створювало повідомлення (і чернетку ПКО)
    // з від'ємною сумою; сума < 1 після intval на беку однаково відхиляється
    if (!this.payment.pay_summa || this.payment.pay_summa === '' || !(Number(this.payment.pay_summa) >= 1)) {
      this.payment.pay_summa_error = true;
      this.isValidationForm = true;
    } else {
      this.payment.pay_summa_error = false;
    }
  }

  orderCheckedValidation() {
    if (this.ordersForPay) {
      this.checkedOrder = !!this.ordersForPay.find(el => el.isOrderChecked === true);

      if (!this.checkedOrder) {
        this.isValidationForm = true;
      }
    }
  }

  commentValidation() {
    if (this.payment.pay_card) {
      // header/data перечитує cards (зміна валюти, боргу), а обрана карта лишається після closeModal -
      // якщо її більше немає в списку, find() давав undefined і «Відправити» падала з TypeError
      const card = this.cards?.find(el => String(el.id) === String(this.payment.pay_card));

      if (!card) {
        this.payment.pay_card = '';
        this.selectedCardFop = null;
        this.payment.pay_card_error = true;
        this.payment.pay_comment_error = false;
        this.isValidationForm = true;
        return;
      }

      const cashPayment = (card.bank_name || '').toLowerCase().includes('готівка');

      if (cashPayment && this.payment.comment === '') {
        this.payment.pay_comment_error = true;
        this.isValidationForm = true;
      } else {
        this.payment.pay_comment_error = false;
      }
    } else {
      this.payment.pay_comment_error = false;
    }
  }

  sendPayment() {
    if (this.isSendingPayNotify) {
      return;
    }

    this.isValidationForm = false;

    let checkedOrdersIds: number[] = [];

    if (this.ordersForPay.length) {
      for (let orders of this.ordersForPay) {
        if (orders.isOrderChecked) {
          checkedOrdersIds.push(parseInt(String(orders.id)));
        }
      }
    }

    this.payCardValidation();
    this.paySummaValidation();
    if (this.ordersForPay.length && checkedOrdersIds.length <= 0) {
      this.orderCheckedValidation();
    }
    this.commentValidation();

    if (!this.isValidationForm) {
      const isFop = this.selectedCardFop?.req_type === 'fop_req';

      let chousedKeyForSend = '';
      if (isFop) {
        if (this.payment.choused_key === 'key' && this.selectedCardFop?.card_number) {
          chousedKeyForSend = `key: ${this.selectedCardFop.card_number}`;
        } else if (this.payment.choused_key === 'iban' && this.selectedCardFop?.iban) {
          chousedKeyForSend = `iban: ${this.selectedCardFop.iban}`;
        }
      }

      this.isSendingPayNotify = true;

      this.http.post(this.apiRoot + 'price/pay_notify',
        {
          pay_card: this.payment.pay_card,
          pay_summa: this.payment.pay_summa,
          pay_currency: this.payment.pay_currency,
          comment: this.payment.comment,
          send_pay_notify: this.payment.send_pay_notify,
          checked_orders_ids: checkedOrdersIds,
          choused_key: chousedKeyForSend,
        })
        .pipe(
          takeUntil(this.isDestroyed),
          finalize(() => this.isSendingPayNotify = false)
        )
        .subscribe((data: RequestData) => {
          if (data.error === 'payments') {
            if (confirm(`Ваша оплата у обробці. Ви впевнені, що зробили ще один платіж та необхідно додатково повідомити про оплату???`)) {
              this.payment.send_pay_notify = true;
              // повтор - після finalize поточного запиту, інакше його відсіче isSendingPayNotify
              setTimeout(() => this.sendPayment());
            }
          } else if (data.error === 'no pay_card') {
            this.payment.pay_card_error = true;
          } else if (data.error === 'no pay_summa') {
            this.payment.pay_summa_error = true;
          } else if (data.error === 'no pay_currency') {
            this.payment.pay_summa_error = true;
          } else if (data.error === 'card not exist') {
            this.payment.pay_card_error = true;
          } else if (data.data === 'success') {
            this.isModalPaymentShow = false;
            this.payment.send_pay_notify = false;
            this.modalSendPaymentChange.emit();
            this.payment.pay_card = '';
            this.payment.pay_summa = '';
            this.payment.comment = '';
            this.payment.choused_key = '';
            this.payment.choused_key_error = false;
            this.isDarkBackground = false;
          }
        });
    }
  }

  closeModal() {
    this.isModalPaymentShow = false;
    this.isModalCardsShow = false;
    this.isDarkBackground = false;
    this.payment.choused_key = '';
    this.payment.choused_key_error = false;
  }

  ngOnDestroy() {
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  }
}
